import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { BookUser, StickyNote } from 'lucide-react';
import { api } from '../api';
import { DeskSection, EmptyNote, formatMoment } from './desk';
import { Button } from './ui/button';

/**
 * The customer file's memory (#204) on the customer page: what the agent knows about this customer across emails, calls,
 * promises and notes, and a "Note about this customer" box for owners and approvers. Figures stay in the ledger sections
 * above; this card holds words only.
 */
interface Fact { id: string; kind: string; text: string; source: 'ledger' | 'person' | 'model' | 'customer'; sourceEventId: string;
  confidence: number; asOf: string }
interface MemoryView {
  customerId: string; canNote: boolean;
  memory: { version: number; summary: string; summarySource: 'model' | 'rules'; updatedAt: string; stale: boolean; facts: Fact[] } | null;
}
const kindLabels: Record<string, string> = {
  preferred_channel: 'Channel', preferred_contact: 'Contact', ap_process: 'AP process', payment_pattern: 'Paying',
  person: 'People', commitment: 'Open', dispute: 'Dispute', preference: 'Preference',
};
const sourceLabels: Record<Fact['source'], string> = { ledger: 'ledger', person: 'team', model: 'inferred', customer: 'they said' };

export function CustomerFileCard({ customerId, persona, asOf }: { customerId: string; persona: string; asOf: string }) {
  const path = `/app-api/customers/${encodeURIComponent(customerId)}/memory`;
  const [view, setView] = useState<MemoryView | null>(null); const [error, setError] = useState('');
  const [text, setText] = useState(''); const [busy, setBusy] = useState(false); const [saved, setSaved] = useState('');
  const load = useCallback(async () => {
    try { setView(await api<MemoryView>(path)); setError(''); }
    catch { setError(`What ${persona} knows about this customer could not load.`); }
  }, [path, persona]);
  useEffect(() => { void load(); }, [load]);
  async function save(event: FormEvent) {
    event.preventDefault();
    const note = text.trim(); if (!note || busy) return;
    setBusy(true); setSaved('');
    try {
      await api(`/app-api/customers/${encodeURIComponent(customerId)}/notes`, { text: note });
      setText(''); setSaved(`Saved. ${persona} reads it on the next email or call.`); await load();
    } catch (e) { setSaved(e instanceof Error ? e.message : 'The note could not be saved.'); }
    finally { setBusy(false); }
  }
  const memory = view?.memory ?? null;
  const notes = memory?.facts.filter(f => f.source === 'person').slice().reverse() ?? [];
  const facts = memory?.facts.filter(f => f.source !== 'person') ?? [];
  return <DeskSection title={`What ${persona} knows`} className="customer-file"
    aside={memory ? <small className="muted">Updated {formatMoment(memory.updatedAt, asOf)}
      {memory.summarySource === 'rules' ? ' · from the history' : ''}</small> : undefined}>
    {error && <p className="muted" role="alert">{error}</p>}
    {!view && !error && <p className="muted" role="status">Reading the customer file…</p>}
    {view && !memory && <EmptyNote icon={<BookUser size={20} aria-hidden />} title="Nothing on file yet">
      {persona} writes this after the first email, reply, call or payment.</EmptyNote>}
    {memory && <>
      <div className="customer-file-summary">{memory.summary.split('\n').filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}</div>
      {facts.length > 0 && <ul className="customer-file-facts">{facts.map(f => <li key={f.id}>
        <span className="desk-badge">{kindLabels[f.kind] ?? f.kind}</span> {f.text}
        <small className="muted"> · {sourceLabels[f.source]}</small></li>)}</ul>}
    </>}
    {notes.length > 0 && <ul className="customer-file-notes" aria-label="Notes from the team">{notes.map(n => <li key={n.id}>
      <StickyNote size={13} aria-hidden /><span>{n.text}</span><small className="muted">{formatMoment(n.asOf, asOf)}</small></li>)}</ul>}
    {view?.canNote && <form className="ask-form customer-file-form" onSubmit={e => void save(e)}>
      <label className="sr-only" htmlFor={`note-${customerId}`}>Note about this customer</label>
      <textarea id={`note-${customerId}`} rows={2} maxLength={1000} value={text} placeholder="Note about this customer (for example: AP pays on the 15th run)"
        onChange={e => setText(e.target.value)} />
      <Button type="submit" size="sm" disabled={busy || !text.trim()}>Save note</Button>
    </form>}
    {saved && <p className="ask-note" role="status">{saved}</p>}
  </DeskSection>;
}
